import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { destroy } from 'redux-form/immutable';
import { trackEvent, trackModal } from 'googleTagManagerMiddleware';
import AuthWrapper from 'components/AuthWrapper';
import { isEmployeeEnv } from 'utils/constants';
import { hashPinToSHA256 } from 'utils/crytpo';
import Validators from 'utils/fieldValidators';
import { COUNTRY_STATES } from 'utils/geographicData';
import { isLocationPls } from 'utils/locations';
import { collapseWhitespace } from 'utils/strings';
import { parseQuery } from 'utils/web';
import CreateCardFormPage from './CreateCardForm/CreateCardFormPage';
import DisabledFormPage from './DisabledForm/DisabledFormPage';
import EmployeeFormPage from './EmployeeForm/EmployeeFormPage';
import ExistingMemberFormPage from './ExistingMemberForm/ExistingMemberFormPage';
import LocationPreferenceFormPage from './LocationPreferenceForm/LocationPreferenceFormPage';
import NewMemberFormPage from './NewMemberForm/NewMemberFormPage';
import StartFormPage from './StartForm/StartFormPage';
import SuccessFormPage from './SuccessForm/SuccessFormPage';
import { ASYNC_VALIDATION_FIELDS, FORM_NAME } from './formConstants';
import './SignUpPage.css';

const disabledFlow = [DisabledFormPage];

const employeeFlow = [EmployeeFormPage, SuccessFormPage];

const newMemberCreateCardFlow = [
  StartFormPage,
  LocationPreferenceFormPage,
  NewMemberFormPage,
  CreateCardFormPage,
  SuccessFormPage
];

const existingMemberFlow = [
  StartFormPage,
  LocationPreferenceFormPage,
  ExistingMemberFormPage,
  SuccessFormPage
];

class SignUpPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hasCard: undefined,
      formPage: 0,
      initialValues: undefined
    };

    const params = parseQuery(this.props.location.search);
    if (
      !isEmployeeEnv() &&
      !this.props.signUpDisabled &&
      params.hasOwnProperty('acct')
    ) {
      const defaultAccount = params.acct;
      this.state = {
        hasCard: true,
        formPage: 1,
        initialValues: !Validators.rewardsNumber(defaultAccount)
          ? { existingRewardsNumber: defaultAccount }
          : undefined
      };
    }

    this.asyncValidate = this.asyncValidate.bind(this);
    this.formRenderer = this.formRenderer.bind(this);
    this.triggerState = this.triggerState.bind(this);
    this.goToPreviousPage = this.goToPreviousPage.bind(this);
    this.goToNextPage = this.goToNextPage.bind(this);
    this.cancelForm = this.cancelForm.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.showFirstErrorField = this.showFirstErrorField.bind(this);
  }

  componentDidMount() {
    this.focusTop();
    trackModal('signup/start');
  }

  componentWillUnmount() {
    // Clear the redux form state
    this.props.destroyForm();
    // Clear any cached apollo data (patron status)
    this.props.client.resetStore();
  }

  asyncValidate(values, dispatch, props, blurredField) {
    const input = this.prepareAsyncValidationValues(values, blurredField);
    if (input) {
      return this.props.validateFields(input).then(errors => {
        if (errors) {
          const oldErrors = props.asyncErrors ? props.asyncErrors.toJS() : {};
          const allErrors = Object.assign({}, oldErrors, errors);
          throw allErrors;
        } else {
          return undefined;
        }
      });
    } else {
      return Promise.resolve();
    }
  }

  triggerState(state, submitAfter = true) {
    if (submitAfter) {
      this.setState(state, () => {
        if (submitAfter === true) {
          submitAfter = this.getSubmitButtonDetails().onSubmit;
        }
        submitAfter();
      });
    } else {
      this.setState(state);
    }
  }

  async submitForm(data) {
    const input = await this.prepareValues(data);

    trackEvent('signup', 'submitting');
    return this.props.onFormSubmit(input).then(result => {
      if (result.success) {
        trackEvent('signup', 'submitted');
        this.goToNextPage();
      }
      return result;
    });
  }

  isSignUpDisabled() {
    return (
      this.props.shouldDisplaySignUp && !this.props.shouldDisplaySignUp.enabled
    );
  }

  getPageFlow(state = null) {
    if (this.isSignUpDisabled()) {
      return disabledFlow;
    }
    if (isEmployeeEnv()) {
      return employeeFlow;
    }
    state = state || this.state;
    return state.hasCard ? existingMemberFlow : newMemberCreateCardFlow;
  }

  getLastPage(state = null) {
    return Math.max(0, this.getPageFlow(state).length - 1);
  }

  getSubmitPage(state = null) {
    return Math.max(0, this.getPageFlow(state).length - 2);
  }

  getPage(page, state = null) {
    const pageFlow = this.getPageFlow(state);
    return pageFlow[Math.max(0, Math.min(pageFlow.length - 1, page))];
  }

  setPage(formPage, scrollToTop = true) {
    this.setState(state => {
      formPage = Math.max(0, Math.min(this.getLastPage(state), formPage));
      const modalEventPath =
        formPage === 0
          ? 'signup/start'
          : `signup/${state.hasCard ? 'existing' : 'new'}/page${formPage}`;
      trackModal(modalEventPath);
      if (scrollToTop) {
        window.scrollTo(0, 0);
        this.focusTop();
      }

      return { formPage };
    });
  }

  goToNextPage() {
    this.setPage(this.state.formPage + 1);
  }

  goToPreviousPage() {
    this.setPage(this.state.formPage - 1);
  }

  cancelForm() {
    this.props.redirectToLogin();
  }

  focusTop() {
    // Use timeout to wait for any pending dom updates
    setTimeout(() => {
      // Focus the first element on page change for keyboard navigation
      const focusEl = document.querySelector(
        '.sign-up-form .auth .focus-start'
      );
      focusEl && focusEl.focus();
    }, 0);
  }

  hashPin = async (location, rewardsNumber, pin) => {
    if (isLocationPls(location)) {
      const rewardsNumberAndPin = rewardsNumber.toString() + pin.toString();
      return await hashPinToSHA256(rewardsNumberAndPin);
    } else {
      return pin;
    }
  };

  prepareAsyncValidationValues(data, blurredField) {
    const values = ASYNC_VALIDATION_FIELDS.reduce((result, field) => {
      // blurredField is undefined when submitting, validate all fields in that case
      if (!blurredField || blurredField === field) {
        const value = data.get(field);
        if (value) {
          result[field] = value;
        }
      }
      return result;
    }, {});

    if (values.hasOwnProperty('email')) {
      values.email = values.email && values.email.trim();
      if (!values.email) {
        delete values.email;
      }
    }
    if (values.hasOwnProperty('displayName')) {
      values.displayName = collapseWhitespace(values.displayName);
      if (!values.displayName) {
        delete values.displayName;
      }
    }

    if (!Object.keys(values).length) {
      return null;
    }

    return values;
  }

  async prepareValues(data) {
    const hasCard = !isEmployeeEnv() && this.state.hasCard;
    const values = data.toJS();

    values.email = values.email && values.email.trim();
    values.displayName = collapseWhitespace(values.displayName);

    delete values.confirmEmail;

    if (hasCard) {
      const encryptedPin = await this.hashPin(
        values.preferredLocation,
        values.existingRewardsNumber,
        values.pin
      );
      values.pin = encryptedPin;

      // Remove values not relevant to hasCard status
      delete values.firstName;
      delete values.lastName;
      delete values.dateOfBirth;
      delete values.gender;
      delete values.address;
      delete values.city;
      delete values.state;
      delete values.zipCode;
      delete values.country;
      delete values.mobilePhone;
    } else {
      delete values.existingRewardsNumber;
      delete values.pin;
      values.preferredLocation = collapseWhitespace(values.preferredLocation);
      values.firstName = collapseWhitespace(values.firstName);
      values.lastName = collapseWhitespace(values.lastName);
      values.address = collapseWhitespace(values.address);
      values.gender = collapseWhitespace(values.gender);
      values.preferredLanguage = collapseWhitespace(values.preferredLanguage);
      values.city = collapseWhitespace(values.city);
      values.state = collapseWhitespace(values.state);
      values.zipCode = collapseWhitespace(values.zipCode);
      values.country = collapseWhitespace(values.country);
      values.mobilePhone = collapseWhitespace(values.mobilePhone);
      if (!COUNTRY_STATES[values.country]) {
        // If the selected country doesn't use states, make sure the value is cleared
        delete values.state;
      }
    }

    return values;
  }

  showFirstErrorField(errors, dispatch) {
    const scrollPadding = 20;
    const pageMap = createFlowPageMap(this.getPageFlow());
    let firstPage = undefined;
    for (let key in errors) {
      let errorPage = pageMap[key];
      if (
        errors.hasOwnProperty(key) &&
        errors[key] &&
        errorPage !== undefined &&
        (errorPage < firstPage || firstPage === undefined)
      ) {
        firstPage = errorPage;
      }
    }
    if (firstPage !== undefined) {
      this.setPage(firstPage, false);
    }

    // Use timeout to wait until the form fields are updated with error classes
    setTimeout(() => {
      const scrollEl = document.querySelector('.sign-up-form .auth');
      const errorEl =
        document.querySelector('.sign-up-form .has-error input') ||
        document.querySelector('.sign-up-form .has-error');

      if (errorEl) {
        if (scrollEl && scrollEl.scrollTop) {
          scrollEl.scrollTop = errorEl.offsetTop - scrollPadding;
        } else if (errorEl.scrollIntoView) {
          errorEl.scrollIntoView();
        }
        errorEl.focus();
      }
    }, 0);
  }

  formRenderer(pageProps, body) {
    const { handleSubmit } = pageProps;

    return <form onSubmit={handleSubmit}>{body}</form>;
  }

  getSubmitButtonDetails() {
    const isSubmitPage = this.state.formPage === this.getSubmitPage();
    const onSubmit = isSubmitPage ? this.submitForm : this.goToNextPage;
    const submitText = isSubmitPage ? 'Sign Up' : 'Continue';
    return { onSubmit, submitText };
  }

  getBackButtonDetails() {
    const isFirstPage = this.state.formPage === 0;
    const backText = 'Go Back';
    const onBack = isFirstPage ? this.cancelForm : this.goToPreviousPage;
    return { onBack, backText };
  }

  render() {
    const { shouldDisplaySignUp } = this.props;
    const { formPage, initialValues, hasCard } = this.state;
    const PageClass = this.getPage(formPage);
    const { onSubmit, submitText } = this.getSubmitButtonDetails();
    const { onBack, backText } = this.getBackButtonDetails();

    return (
      <div className="sign-up-form">
        <Helmet>
          <title>Member Sign Up</title>
        </Helmet>
        <AuthWrapper
          aria-label="Sign Up Form"
          logoName="signup"
          onBack={onBack}
        >
          <h1 className="sr-only">Sign Up for Wind Creek Casino</h1>
          <div className="form-scroll-child">
            <PageClass
              triggerState={this.triggerState}
              formRenderer={this.formRenderer}
              onBack={onBack}
              backText={backText}
              onSubmit={onSubmit}
              submitText={submitText}
              hasCard={hasCard}
              form={FORM_NAME}
              onSubmitFail={this.showFirstErrorField}
              asyncValidate={this.asyncValidate}
              asyncBlurFields={ASYNC_VALIDATION_FIELDS}
              initialValues={initialValues}
              shouldDisplaySignUp={shouldDisplaySignUp}
            />
          </div>
        </AuthWrapper>
      </div>
    );
  }
}

function createFlowPageMap(flow) {
  // Create a map of "fieldName" -> pageIndex
  return flow
    .map((formPage, pageIndex) =>
      formPage.FormFields.reduce((acc, field) => {
        acc[field] = pageIndex;
        return acc;
      }, {})
    )
    .reduce((acc, val) => Object.assign(acc, val), {});
}

function mapDispatchToProps(dispatch) {
  return {
    redirectToLogin: () => dispatch(push('/login')),
    destroyForm: () => dispatch(destroy(FORM_NAME))
  };
}

SignUpPage.propTypes = {
  onFormSubmit: PropTypes.func,
  shouldDisplaySignUp: PropTypes.object
};

export default withRouter(
  connect(null, mapDispatchToProps)(withApollo(SignUpPage))
);
